/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';

import { Popover } from '@im/ui-lib/zenith/Popover';
import { HorizontalTabs } from '@im/ui-lib/zenith/Tabs';
import { Icon } from '@im/ui-lib/zenith/Icon';
import { StatusBadge } from '@im/ui-lib/zenith/StatusBadge';

import { FeaturedIcon } from '@im/ui-lib/zenith/FeaturedIcon';
import { ImThemeProviderZenith } from '@im/im-theme';
import { MuiProviders } from '@im/ui-lib/theme/MuiProviders';
import { DropdownMenu } from '@im/ui-lib/zenith/DropdownMenu/DropdownMenu';
import { EmptyStateIllustration } from '@im/ui-lib/zenith/EmptyStateIllustration';
import { useMarkAsReadNotifications } from '../../hooks/use-markAsReadForNotifications';
import { useFirebaseLoader } from '../../app/injectors/useFirebase';
import { useFeatureFlag } from 'mf_core/feature-flag';
import { FCMEnabler } from './FCMEnabler';
import { useNavigate } from 'react-router-dom';
import { useIsImpersonationMode } from 'mf_core/auth';

export function NotificationZenith() {
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [unreadMessagesHistoryCount, setUnreadMessagesHistoryCount] =
    useState<any>([]);
  const handleTabChange = (event: React.SyntheticEvent, newKey: string) => {
    setSelectedTabKey(newKey);
  };
  const { isReady, value: boData } = useFeatureFlag([
    'im-customer-usermanagement-notificationsettingzenithview-isenabled',
    'im-customer-usermanagement-notificationsettingzenithview-isenabled-whitelistedresellers',
  ]);
  const firebaseReady = useFirebaseLoader();
  const useMarkAsReadNotification = useMarkAsReadNotifications();

  const showNotificationBellIcon = useMemo(() => {
    return (
      boData[
        'im-customer-usermanagement-notificationsettingzenithview-isenabled'
      ] === 'true' &&
      boData[
        'im-customer-usermanagement-notificationsettingzenithview-isenabled-whitelistedresellers'
      ] === 'true'
    );
  }, [boData]);
  const notificationTabs = [
    {
      badge: true,
      badgeLabel: unreadMessagesHistoryCount,
      'data-name': 'Unread_Notifications',
      key: 'Unread',
      label: 'Unread',
    },
    { 'data-name': 'All_Notifications', key: 'All', label: 'All' },
  ];
  const [selectedTabKey, setSelectedTabKey] = useState(notificationTabs[0].key);

  const [chat, setChat] = useState<any>([]);
  const [historyChat, setHistoryChat] = useState<any>([]);
  const latestChat = useRef<any>(null);
  latestChat.current = chat;
  const [fcmToken, setFcmtoken] = useState('');
  const isImpersonationMode = useIsImpersonationMode();

  useEffect(() => {
    const mergedChat = [...chat, ...historyChat];
    const unique = removeDuplicate(mergedChat);
    const unread = unique.filter((item) => item?.isRead === false);
    setUnreadMessagesHistoryCount(unread.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat, historyChat]);

  const listener = useCallback((event: MessageEvent) => {
    const updatedChat = [...latestChat.current];

    const incomingId =
      event.data?.data?.notificationId || event.data?.notificationId;

    const isDuplicate = updatedChat.some((item) => {
      const existingId = item?.data?.notificationId || item?.notificationId;
      return existingId && incomingId && existingId === incomingId;
    });

    if (event?.data && !isDuplicate) {
      const newMessage = {
        ...event.data,
        content: event.data?.data?.content ?? '',
        createdDate: new Date().toISOString(),
        isRead: false,
        notificationId:
          event.data?.data?.notificationId ?? event.data?.notificationId,
        title: event.data?.data?.title ?? '',
      };

      const newChatArray = [...updatedChat, newMessage];
      const deduplicated = removeDuplicate(newChatArray);
      setChat(deduplicated);
    }

    return () => {
      navigator?.serviceWorker?.removeEventListener('message', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   navigator?.serviceWorker?.addEventListener('message', listener);
  //   return () => {
  //     navigator?.serviceWorker?.removeEventListener('message', listener);
  //   };
  // }, [listener]);
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof navigator.serviceWorker?.addEventListener === 'function'
    ) {
      navigator.serviceWorker.addEventListener('message', listener);
      return () => {
        navigator.serviceWorker.removeEventListener('message', listener);
      };
    } else {
      console.warn('Service worker not supported. Skipping listener setup.');
    }
  }, [listener]);

  const markAllAsRead = async () => {
    const notificationIds = allNotifications
      .map((item) => item?.data?.notificationId || item?.notificationId)
      .filter(Boolean);
    setChat([]);
    setIsPopoverOpen(true);
    useMarkAsReadNotification.mutate({ notificationIds });
  };

  const getNotificationId = (item: any) =>
    item?.data?.notificationId || item?.notificationId;

  const markAsReadItem = (item: any) => {
    setChat([]);
    useMarkAsReadNotification.mutate({
      notificationIds: [getNotificationId(item), 'read'],
    });
  };

  const dismiss = (item: any) => {
    setChat([]);
    useMarkAsReadNotification.mutate({
      notificationIds: [getNotificationId(item), 'delete'],
    });
  };

  const onNotificationItemClick = (item: any) => {
    setChat([]);
    useMarkAsReadNotification.mutate({
      notificationIds: [getNotificationId(item), 'read'],
    });
  };

  const removeDuplicate = (notifications: any[]) => {
    const result: any[] = [];

    notifications.forEach((item, index, self) => {
      const id = getNotificationId(item);
      if (!id) return;

      const isFirst =
        index ===
        self.findIndex((data) => {
          return getNotificationId(data) === id;
        });

      if (isFirst) {
        result.push(item);
      }
    });

    result.sort(
      (a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );

    return result.map((item) => {
      const content = item.content ?? '';
      const anchorTagRegex = /<a\s+href="(.*?)">(.*?)<\/a>/g;

      const elements: (string | JSX.Element)[] = [];
      let lastIndex = 0;
      let match;

      while ((match = anchorTagRegex.exec(content)) !== null) {
        if (match.index > lastIndex) {
          elements.push(content.substring(lastIndex, match.index));
        }

        const [fullMatch, href, text] = match;

        elements.push(
          <a
            href={href}
            key={`${href}-${match.index}`}
            rel="noopener noreferrer"
            style={{ color: '#1976d2', textDecoration: 'underline' }}
            target="_blank"
          >
            {text}
          </a>
        );

        lastIndex = match.index + fullMatch.length;
      }

      return {
        action: undefined,
        content: parseNotificationContent(item.content ?? ''),
        ...item,
      };
    });
  };
  const parseNotificationContent = (content: string): JSX.Element => {
    const anchorTagRegex = /<a\s+href="(.*?)">(.*?)<\/a>/g;

    const elements: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = anchorTagRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        elements.push(content.substring(lastIndex, match.index));
      }

      const [fullMatch, href, text] = match;

      elements.push(
        <a
          href={href}
          key={`${href}-${match.index}`}
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'underline' }}
          target="_blank"
        >
          {text}
        </a>
      );

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < content.length) {
      elements.push(content.substring(lastIndex));
    }

    return <span>{elements}</span>;
  };

  const allNotifications = useMemo(() => {
    return removeDuplicate([...chat, ...historyChat]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat, historyChat]);
  const filteredNotifications = useMemo(() => {
    if (selectedTabKey === 'Unread') {
      return allNotifications.filter((item: any) => item?.isRead === false);
    }
    return allNotifications;
  }, [allNotifications, selectedTabKey]);

  const notificationIconRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      {showNotificationBellIcon && !isImpersonationMode && (
        <IconButton
          onClick={() => setIsPopoverOpen(true)}
          ref={notificationIconRef}
        >
          {/* For zenith implementation: replace <Badge> with <StatusBadge>: */}
          {/* <StatusBadge
                label={123}
                max={999}
                showZero
                sx={{ fontSize: '12px' }}
              > */}
          <Badge
            badgeContent={unreadMessagesHistoryCount}
            color="primary"
            max={999}
            sx={{
              '& .MuiBadge-anchorOriginTopRightRectangular.MuiBadge-badge': {
                width: 'fit-content',
              },
            }}
          >
            <Icon iconId="BellOne" />
          </Badge>
          {/* </StatusBadge> */}
        </IconButton>
      )}
      <ImThemeProviderZenith>
        <MuiProviders>
          {isReady && showNotificationBellIcon && (
            <FCMEnabler
              chat={chat}
              fcmToken={fcmToken}
              firebaseReady={firebaseReady}
              historyChat={historyChat}
              latestChat={latestChat}
              setChat={setChat}
              setFcmtoken={setFcmtoken}
              setHistoryChat={setHistoryChat}
            />
          )}
          {showNotificationBellIcon && !isImpersonationMode && (
            <Box>
              <Popover
                actions
                anchorEl={notificationIconRef}
                buttonXClose={false}
                featuredIcon="AlertCircle"
                featuredIconColor="gray"
                featuredIconSize="sm"
                featuredIconTheme="outline"
                footerDivider
                footerType="horizontal-right-aligned"
                headerText="Notifications"
                hideHeader
                onClose={() => setIsPopoverOpen(false)}
                open={isPopoverOpen}
                popoverTriggerProps={{
                  triggerType: 'Custom',
                }}
                secondaryButtonProps={{
                  id: 'secondary',
                  onClick: markAllAsRead,
                  startIcon:
                    unreadMessagesHistoryCount > 0 ? (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Stack alignItems="center" direction="row" spacing={1}>
                          <Icon iconId="CheckCircleBroken" />
                          <Typography
                            data-name="Mark all as read_Notifications"
                            variant="text-sm-semibold"
                          >
                            Mark all as read
                          </Typography>
                        </Stack>
                      </>
                    ) : null,
                }}
                showFeaturedIcon
                tipPlacement="Left"
                width={420}
              >
                <>
                  <Stack
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    px={2}
                    py={1}
                  >
                    <Typography variant="text-lg-semibold">
                      Notifications
                    </Typography>
                    <FeaturedIcon
                      Icon="SettingsTwo"
                      color="gray"
                      onClick={() => {
                        navigate('/my/account/AccountSettings', {
                          state: { selectedTab: 'notificationSetting' },
                        });
                      }}
                      size="sm"
                      sx={{ cursor: 'pointer' }}
                    />
                  </Stack>

                  <Box px={2}>
                    <HorizontalTabs
                      aria-label="activity tabs"
                      fullWidth
                      onTabChange={handleTabChange}
                      selectedTabKey={selectedTabKey}
                      size="sm"
                      tabs={notificationTabs}
                      type="underline"
                    />
                  </Box>

                  <Box
                    maxHeight="360px"
                    maxWidth="420px"
                    sx={{
                      boxSizing: 'border-box',
                      mb: 1,
                      mt: 1,
                      overflowX: 'hidden',
                      overflowY: 'auto',
                      pr: 1,
                      px: 2,
                    }}
                    width="100%"
                  >
                    {filteredNotifications.length === 0 ? (
                      <Stack
                        alignItems="center"
                        height={300}
                        justifyContent="center"
                      >
                        <EmptyStateIllustration
                          id="bin"
                          sx={{ height: '144px', width: '144px' }}
                        />
                        <Typography variant="text-xl-semibold">
                          You are all up to date
                        </Typography>
                        <Typography>
                          There are no unread notifications
                        </Typography>
                      </Stack>
                    ) : (
                      filteredNotifications.map((item, i) => (
                        <Stack
                          alignItems="flex-start"
                          direction="row"
                          key={i}
                          pt={2}
                          spacing={2}
                          sx={{ width: '100%', wordBreak: 'break-word' }}
                        >
                          <Stack
                            alignItems="center"
                            justifyContent="center"
                            position="relative"
                            width="32px"
                          >
                            {!item?.isRead && (
                              <StatusBadge
                                append
                                status="default"
                                sx={{
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                }}
                                type="dot"
                              />
                            )}
                            <FeaturedIcon
                              Icon={item?.isRead ? 'PackageCheck' : 'Package'}
                              color="gray"
                              iconTheme="light-circle"
                              size="sm"
                            />
                          </Stack>

                          <Stack
                            alignItems="flex-start"
                            direction="column"
                            onClick={() => onNotificationItemClick(item)}
                            spacing={0.5}
                            width="100%"
                          >
                            <Typography variant="text-sm-semibold">
                              {item.title || 'No title'}
                            </Typography>
                            <Typography variant="text-sm-medium">
                              {parseNotificationContent(item.content) ||
                                'No content'}
                            </Typography>

                            <Typography variant="text-xs-regular">
                              {new Date(item.createdDate).toLocaleDateString()}
                            </Typography>
                          </Stack>

                          <Stack>
                            <DropdownMenu
                              menuItemProps={[
                                {
                                  content: 'Mark as read',
                                  'data-name': 'Markasread_Notifications',
                                  disabled: item?.isRead,
                                  id: 'markasread',
                                  onClick: () => markAsReadItem(item),
                                },
                                {
                                  content: 'Dismiss',
                                  'data-name': 'Dismiss_Notifications',
                                  id: 'dismiss',
                                  onClick: () => dismiss(item),
                                },
                              ]}
                              menuMaxWidth="240px"
                              menuTriggerProps={{
                                icon: <Icon iconId="DotsHorizontal" />,
                                triggerType: 'Icon',
                              }}
                              sx={{ overflow: 'hidden' }}
                            />
                          </Stack>
                        </Stack>
                      ))
                    )}
                  </Box>
                </>
              </Popover>
            </Box>
          )}
        </MuiProviders>
      </ImThemeProviderZenith>
    </>
  );
}
